import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  isAfter,
  isBefore,
  isSameDay
} from 'date-fns';

import {
  ParticipantEvent,
  RecurringType
} from '../../../models/ParticipantEvent';
import { ParticipantEventLog } from '../../../models/ParticipantEventLog';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { log } from '../../../tools/LogHelper';
import { utcToZonedTime } from 'date-fns-tz';

export interface ParticipantEventOccurrence {
  participantEvent: ParticipantEvent;
  participantEventLog?: ParticipantEventLog;
  intendedStartDate: Date;
  occurrenceCount: number;
  anyTimeDayOf: boolean;
  progress?: number;
  studyParticipant?: StudyParticipant;
}

/**
 * Determines which of the 2 occurrences has a more recent intended start date
 * @param occurrence1
 * @param occurrence2
 * @returns the occurrence with the most recent intended start date
 */
export function minEventOccurrence(
  occurrence1?: ParticipantEventOccurrence,
  occurrence2?: ParticipantEventOccurrence
) {
  if (!occurrence1) return occurrence2;
  if (!occurrence2) return occurrence1;
  return isBefore(occurrence1.intendedStartDate, occurrence2.intendedStartDate)
    ? occurrence1
    : occurrence2;
}

/**
 * Last calendar day the occurrence remains available, based on expireDayCount
 * (defaults to 3). Matches getAllEventOccurrences expiration: available while
 * today < activityDate + expireDayCount.
 */
export function getOccurrenceExpirationDate(
  intendedStartDate: Date,
  expireDayCount?: number | null
): Date {
  const days = expireDayCount === 0 ? 0 : (expireDayCount ?? 3);
  return addDays(intendedStartDate, Math.max(days - 1, 0));
}

/**
 * Generate all occurrences of a participant event from the event's start date
 * through it's max occurrences or up to the max future occurrence settings
 * (default max future occurrence is set to 100). A non-recurring event would
 * return a single occurrence.The function returns an object containing:
 * - next - the next occurrence if exists
 * - future - array of future occurrences
 * - past - array of past occurrences from start date
 * - completed - array of completed occurrences
 * - expired - array of exired (not completed in 3 days)
 * - all - array of all occurrences
 * @param event ParticipantEvent
 * @param studyParticipant StudyParticipant record for the event
 * @param options
 * @returns { next, future, past, completed, expired, all }
 */
export function getAllEventOccurrences(
  event: ParticipantEvent,
  studyParticipant: StudyParticipant,
  options?: {
    maxFutureOccurrences?: number;
    hideCompletedStudies?: boolean;
    sort?: 'ASC' | 'DESC';
    timeZone?: string;
  }
) {
  const { maxFutureOccurrences = 100, sort, timeZone } = options || {};
  let future: ParticipantEventOccurrence[] = [];
  let past: ParticipantEventOccurrence[] = [];
  let completed: ParticipantEventOccurrence[] = [];
  let expired: ParticipantEventOccurrence[] = []; // stores events that are not completed within 3 days
  let persistent: ParticipantEventOccurrence[] = [];
  let next: ParticipantEventOccurrence | undefined;
  const occurrence: ParticipantEventOccurrence = {
    participantEvent: event,
    intendedStartDate: event.startDate,
    occurrenceCount: 0,
    anyTimeDayOf: event.anyTimeDayOf,
    studyParticipant
  };
  const maxOccurrences = event.maxOccurrences || maxFutureOccurrences;
  let startDate = getEventOccurrenceDate(event, maxOccurrences);
  if (timeZone) {
    startDate = utcToZonedTime(startDate, timeZone);
  }
  const endOfDay = (
    timeZone ? utcToZonedTime(new Date(), timeZone) : new Date()
  ).setHours(23, 59, 59);
  // console.log('---');
  // console.log('event', event.name);
  do {
    // get the next occurrence start date
    occurrence.intendedStartDate = getEventOccurrenceDate(
      event,
      occurrence.occurrenceCount
    );
    // determine if an event log has already been created for the occurrence
    // check for completed occurrences first before incomplete
    const participantEventLog =
      event.participantEventLogs.find(
        log =>
          log.occurrenceCount === occurrence.occurrenceCount &&
          log.completedDate
      ) ??
      event.participantEventLogs.find(
        log => log.occurrenceCount === occurrence.occurrenceCount
      );

    const newOccurrence = { ...occurrence };
    if (participantEventLog) {
      newOccurrence.participantEventLog = participantEventLog;
    }
    // get all occurrences prior to the withdrawn or screen failure date.
    if (
      (!studyParticipant.withdrawnDate ||
        isBefore(
          newOccurrence.intendedStartDate,
          studyParticipant.withdrawnDate
        )) &&
      (!studyParticipant.screenFailedDate ||
        isBefore(
          newOccurrence.intendedStartDate,
          studyParticipant.screenFailedDate
        ))
    ) {
      if (newOccurrence.participantEvent.allowMultipleEntries) {
        persistent.push(newOccurrence);
        if (newOccurrence.participantEventLog?.completedDate) {
          completed.push(newOccurrence);
        }
      } else if (newOccurrence.participantEventLog?.completedDate) {
        completed.push(newOccurrence);
      } else if (
        // any activities that exceed its expire day count will be marked expired.
        // mark incomplete activities as expired when the current date is after the withdrawn date.
        isBefore(
          newOccurrence.intendedStartDate,
          addDays(
            endOfDay,
            event.expireDayCount === 0 ? 0 : -(event.expireDayCount || 3)
          )
        ) ||
        (studyParticipant.withdrawnDate &&
          isAfter(new Date(), studyParticipant.withdrawnDate)) ||
        (studyParticipant.screenFailedDate &&
          isAfter(new Date(), studyParticipant.screenFailedDate)) ||
        studyParticipant.study?.status === 'Locked' ||
        studyParticipant.study?.status === 'Completed'
      ) {
        expired.push(newOccurrence);
      } else if (
        // if anyTimeOfDay then set hours to 11:59:59pm so that any activities
        // today can be complete at any time (don't have to wait till after
        // notification time)
        isBefore(
          newOccurrence.intendedStartDate,
          newOccurrence.anyTimeDayOf ? endOfDay : new Date()
        )
      ) {
        past.push(newOccurrence);
      } else {
        future.push(newOccurrence);
        next = minEventOccurrence(newOccurrence, next);
      }
    }
    // incremenet occurrenceCount before processing next occurrence
    occurrence.occurrenceCount++;
  } while (
    isBefore(occurrence.intendedStartDate, startDate) &&
    occurrence.occurrenceCount < maxOccurrences
  );

  let all: ParticipantEventOccurrence[] = [...future, ...past, ...completed];
  if (sort) {
    future = sortEventOccurrences(future, sort);
    past = sortEventOccurrences(past, sort);
    completed = sortEventOccurrences(completed, sort);
    expired = sortEventOccurrences(expired, sort);
    all = sortEventOccurrences(all, sort);
    persistent = sortEventOccurrences(persistent, sort);
  }
  return { next, future, past, completed, expired, all, persistent };
}

/**
 * Get the start date of a particular participant event occurrence based on the
 * occurrence count. The occurrence count is the number of event instances for
 * recurring events. A non-recurring event will return the event end date.
 * @param event
 * @param occurrenceCount
 * @returns
 */
export function getEventOccurrenceDate(
  event: ParticipantEvent,
  occurrenceCount: number,
  dateType: 'startDate' | 'endDate' = 'startDate'
) {
  const multiplier = (event.separationCount || 0) + 1;
  switch (event.recurringType) {
    case RecurringType.Daily:
      return addDays(event.startDate, occurrenceCount * multiplier);
    case RecurringType.Weekly:
      return addWeeks(event.startDate, occurrenceCount * multiplier);
    case RecurringType.Monthly:
      return addMonths(event.startDate, occurrenceCount * multiplier);
    case RecurringType.Yearly:
      return addYears(event.startDate, occurrenceCount * multiplier);
    default:
      // not recurring (important that the end date is returned here)
      return dateType === 'startDate'
        ? event.startDate
        : event.endDate || event.startDate;
  }
}

/**
 * Helper function for sorting arrays of event occurrences.
 * @param eventOccurrences
 * @param order
 * @returns
 */
export function sortEventOccurrences(
  eventOccurrences: ParticipantEventOccurrence[],
  order: 'ASC' | 'DESC'
) {
  return eventOccurrences.sort((a, b) => {
    const dateCompare =
      order === 'DESC'
        ? b.intendedStartDate.getTime() - a.intendedStartDate.getTime()
        : a.intendedStartDate.getTime() - b.intendedStartDate.getTime();
    if (dateCompare !== 0) return dateCompare;
    // Tiebreaker: respect the coordinator's drag-and-drop order from the
    // admin portal when multiple forms share the same intendedStartDate.
    // Always ascending — `position` is a display ordinal (top-to-bottom
    // in the admin), not a temporal value, so reversing it for a DESC
    // date sort has no semantic meaning.
    const positionA = a.participantEvent.studyTemplateEvent?.position ?? 0;
    const positionB = b.participantEvent.studyTemplateEvent?.position ?? 0;
    if (positionA !== positionB) return positionA - positionB;

    // Tiebreaker for events with the same position
    return (
      (a.participantEvent.studyTemplateEventId ?? 0) -
      (b.participantEvent.studyTemplateEventId ?? 0)
    );
  });
}

/**
 * Helper function for filtering out removed occurrences from custom days.
 * @param eventOccurrences
 * @returns
 */
export function filterOutRemovedOccurrences(
  eventOccurrences: ParticipantEventOccurrence[]
): ParticipantEventOccurrence[] {
  const filteredByRemovedCounts = eventOccurrences.filter(
    e =>
      !(e.participantEvent.removedOccurrenceCounts || []).includes(
        e.occurrenceCount
      )
  );

  const nonStudyTemplateEvent = filteredByRemovedCounts.filter(
    e => !e.participantEvent.studyTemplateEventId
  );
  const studyTemplateEvent = filteredByRemovedCounts.filter(
    e => e.participantEvent.studyTemplateEventId
  );

  if (!studyTemplateEvent.length) {
    return filteredByRemovedCounts;
  }
  const counts = new Map<string, number>();
  const makeKey = (occurrence: ParticipantEventOccurrence) =>
    JSON.stringify([
      occurrence.studyParticipant?.id,
      occurrence.participantEvent.studyTemplateEventId,
      occurrence.participantEvent.repeatCount,
      occurrence.intendedStartDate.getTime()
    ]);
  for (const occurrence of studyTemplateEvent) {
    const key = makeKey(occurrence);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return [
    ...studyTemplateEvent.filter(
      occurrence => counts.get(makeKey(occurrence)) === 1
    ),
    ...nonStudyTemplateEvent
  ];
}

// function getNextEventOccurrence(event: ParticipantEvent) {
//   // if not recurring check if event already occured
//   if (!event.recurringType) {
//     return isBefore(new Date(), event.startDate)
//       ? undefined
//       : {
//           participantEvent: event,
//           intendedStartDate: event.startDate,
//           occurrenceCount: 0
//         };
//   }
// }

export function getStreakFromOccurrences(
  studyParticipantId: number,
  completedOccurrences: ParticipantEventOccurrence[],
  expiredOccurrences: ParticipantEventOccurrence[],
  pastOccurrences: ParticipantEventOccurrence[]
) {
  // NOTE: completed and expired occurrences already sorted in descending order
  const completed = completedOccurrences.filter(
    o => o.studyParticipant?.id === studyParticipantId
  );
  const past = pastOccurrences.filter(
    o => o.studyParticipant?.id === studyParticipantId
  );
  const lastCompleted = completed[0];
  const lastExpired = expiredOccurrences.find(
    o => o.studyParticipant?.id === studyParticipantId
  );

  // get latest expiring soon event
  const latestExpiringSoonEvent = pastOccurrences.find(
    e => !isSameDay(new Date(), e.intendedStartDate)
  );

  // check if all current day events are completed
  const completedTodayEvents = !past.find(o =>
    isSameDay(o.intendedStartDate, new Date())
  );

  // if no completed items then no streak
  if (!lastCompleted) return 0;
  let streak = 0;
  if (lastExpired) {
    // get completed occurrence after last expired occurrence
    const completedAscending = completed.reverse();
    // get index of completed occurrence after expired occurrence
    const completedAfterExpiredIndex = completedAscending.findIndex(o =>
      isAfter(o.intendedStartDate, lastExpired.intendedStartDate)
    );
    // if last expired occurs after all previous completed occurrences return no streak
    if (completedAfterExpiredIndex === -1) return 0;

    // if expired occurs on same day as last completed
    if (
      isSameDay(
        completedAscending[completedAfterExpiredIndex].intendedStartDate,
        lastExpired.intendedStartDate
      )
    )
      return 0;

    // check if the latest expiring soon is more latest than the latest expired
    // then get days based off of the more recent data
    if (
      latestExpiringSoonEvent &&
      isAfter(
        latestExpiringSoonEvent.intendedStartDate,
        lastExpired.intendedStartDate
      )
    ) {
      // get days from last expiring soon
      streak = differenceInDays(
        new Date(),
        addDays(latestExpiringSoonEvent.intendedStartDate, 1) // get the day after expiring soon event
      );
    } else {
      // get days from last completed after expired occurrence
      streak = differenceInDays(
        new Date(),
        completedAscending[completedAfterExpiredIndex].intendedStartDate
      );
    }
  } else {
    // if no expired items then get streak from first completed item
    // if there is a expiring soon event cut off streak count from the latest expiring soon
    const firstCompleted = completed[completed.length - 1];
    streak = differenceInDays(
      new Date(),
      latestExpiringSoonEvent
        ? addDays(latestExpiringSoonEvent.intendedStartDate, 1)
        : firstCompleted.intendedStartDate
    );
  }
  if (completedTodayEvents) {
    // if all events on the current day are completed add 1 streak
    streak++;
  }
  return streak;
}
